import React, { useState } from 'react';
import { Plus, Search, FileText, DollarSign, Calendar } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Card } from '../components/ui/Card';
import { ContractList } from '../components/contracts/ContractList';
import { ContractForm } from '../components/contracts/ContractForm';
import { useAppStore } from '../contexts/AppStore';
import { Contract } from '../data/mockContracts';

export const ContractsPage: React.FC = () => {
  const { contracts, addContract } = useAppStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  const handleCreateContract = (contractData: Omit<Contract, 'id'>) => {
    const newContract: Contract = {
      ...contractData,
      id: `cont-${Date.now()}`,
    };
    
    addContract(newContract);
    setIsModalOpen(false);
  };
  
  // Filtrar contratos
  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = 
      contract.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.stakeholderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.projectName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = !filterType || contract.type === filterType;
    const matchesStatus = !filterStatus || contract.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });
  
  // Calcular estadísticas
  const activeContracts = contracts.filter(c => c.status === 'Vigente').length;
  const totalValue = contracts
    .filter(c => c.status === 'Vigente')
    .reduce((sum, c) => sum + c.value, 0);
  const pendingNegotiation = contracts.filter(c => c.status === 'En Negociación').length;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestión de Contratos</h1>
          <p className="text-gray-600 mt-1">
            Administra contratos de licenciamiento y transferencia tecnológica
          </p>
        </div>
        
        <Button
          variant="primary"
          onClick={() => setIsModalOpen(true)}
          className="flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Nuevo Contrato</span>
        </Button>
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Contratos Vigentes</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{activeContracts}</p>
            </div>
            <div className="bg-green-500 p-4 rounded-lg">
              <FileText size={32} className="text-white" />
            </div>
          </div>
        </Card>
        
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Valor Total Vigente</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                ${(totalValue / 1000000).toFixed(1)}M
              </p>
            </div>
            <div className="bg-blue-500 p-4 rounded-lg">
              <DollarSign size={32} className="text-white" />
            </div>
          </div>
        </Card>
        
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">En Negociación</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{pendingNegotiation}</p>
            </div>
            <div className="bg-orange-500 p-4 rounded-lg">
              <Calendar size={32} className="text-white" />
            </div>
          </div>
        </Card>
      </div>
      
      {/* Filtros y búsqueda */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por título, stakeholder o proyecto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <select 
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Todos los tipos</option>
          <option value="Licencia">Licencia</option>
          <option value="NDA">NDA</option>
          <option value="Colaboración">Colaboración</option>
          <option value="Transferencia">Transferencia</option>
        </select>
        
        <select 
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Todos los estados</option>
          <option value="Borrador">Borrador</option>
          <option value="En Negociación">En Negociación</option>
          <option value="Firmado">Firmado</option>
          <option value="Vigente">Vigente</option>
          <option value="Vencido">Vencido</option>
        </select>
      </div>
      
      {/* Lista de contratos */}
      <ContractList contracts={filteredContracts} />
      
      {/* Modal de creación */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Registrar Nuevo Contrato"
        size="xl"
      >
        <ContractForm
          onSubmit={handleCreateContract}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>
    </div>
  );
};
